import { useEffect, useState, useMemo, useCallback } from 'react';
import cn from 'classnames';
import * as R from 'ramda';

import { ButtonGroup, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { MdFilterList, MdMoreHoriz, MdSave, MdWarning } from 'react-icons/md';

import { useQueryClient } from '@tanstack/react-query';
import { PlanRow, TYPES_TO_NAMES } from '@blisspointmedia/bpm-types/dist/LinearBuying';
import * as Dfns from 'date-fns/fp';

import { useAppSelector } from '@src/redux';
import { useSetAreYouSure, useSetError } from '@src/redux/modals';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { useCreativeMap } from '@src/redux/creative';
import * as UserRedux from '@src/redux/user';
import { useStateFunction, useMap } from '@src/utils/hooks/useData';
import { CURRENT_WEEK_START } from '@src/utils/date-utils';
import { downloadJSONToCSV } from '@src/utils/download-utils';
import { useExperimentFlag } from '@src/utils/experiment-context';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';

import { Page } from '@Components/Page';
import { Spinner } from '@Components/Spinner/Spinner';
import { Button } from '@Components/Button';
import { IconButton } from '@Components/IconButton';
import { DatePicker } from '@Components/DatePicker';
import { FilterBar } from '@Components/FilterBar';
import { Dropdown, DropdownOption, DropdownToggleType } from '@Components/Dropdown';
import {
  Appearance,
  Background,
  Design,
  Intent,
  Placement,
  Size,
  StretchMode
} from '@Components/designConstants';
import {
  FILTER_BAR_OPTIONS,
  DEMO_GROUPINGS,
  LinearBuyingAction,
  LinearBuyingActionType,
  MEASUREMENT_OPTIONS,
  PRETTY_DEMO_NAMES
} from './linear-buying-constants';

import { EditTableView } from './EditTableView';
import { SummaryView } from './SummaryView';
import { PendingChanges } from './PendingChanges/PendingChanges';

import { AddNewNetworkModal } from './Modals/AddNewNetworkModal';
import { BlanketTrafficModal } from './Modals/BlanketTrafficModal';
import { useBlanketTraffic } from './use-blanket-traffic';
import { findCoveringInstruction, getBlanketCombos } from './blanket-traffic-utils';
import { NoRateChangesModal } from './Modals/NoRateChangesModal';
import { ExtendEditModal } from './Modals/ExtendEditModal';
import { ExtendSelectedModal } from './Modals/ExtendSelectedModal';
import { CreativeAllocationModal } from './Modals/CreativeAllocationModal';
import { ImportModal } from './Modals/ImportModal';
import { IconLocalImportModal } from './Modals/IconLocalImportModal';
import { AudiencePickerModal } from './Modals/AudiencePickerModal';

import {
  CampaignMetadata,
  EditsMap,
  makeNewRow,
  checkUpdatedRates,
  RowsByWeek,
  ExtendEditModalData,
  checkForDuplicateRows,
  checkForConstraints,
  checkForImpressionOverrides,
  processExtendSelected,
  makeKeyWithUUID,
  flattenedRowsByWeek,
  compareDates
} from './linear-buying-utils';

import {
  processBulkImport,
  processImportChanges,
  exportToExcel,
  exportSummaryView,
  processXMLImport,
  processIconLocalImport
} from './excel-utils';

import {
  fetchBookedNielsenEstimates,
  fetchDeliveredNielsenEstimates,
  fetchIconLocalMappings,
  fetchLinearPlans,
  LINEAR_PLANS_QUERY_KEY,
  saveLinearPlans,
  useFutureMatchingPlans,
  useLinearConstraints,
  useLinearNetworks,
  useLinearPlans,
  useLinearRates,
  useNielsenEstimates
} from './use-linear-buying';

import './LinearBuying.scss';

export const DATE_FORMAT = 'yyyy-MM-dd';

type SelectedView = 'table' | 'summary';

interface LinearBuyingProps {
  channel?: string;
}

export const LinearBuying = ({ channel = 'linear' }: LinearBuyingProps) => {
  const companyInfo = useCompanyInfo();
  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure();
  const queryClient = useQueryClient();
  const isAdmin = useAppSelector(UserRedux.isAdminSelector);
  const company = companyInfo.cid;
  const { creativeMap, colorMap } = useCreativeMap({
    // For company-specific scratchpads (i.e. instacart_scratchpad), we want to use the actual company's creative map so we can modify traffic.
    company: company.includes('_scratchpad') ? company.split('_')[0] : company,
    streaming: false,
    mediaTypes: channel === 'radio' ? ['audio'] : ['linear']
  });

  const defaultDates = () => {
    const start = R.pipe(Dfns.startOfISOWeek, Dfns.format(DATE_FORMAT))(new Date());
    const end = start;
    return { start, end };
  };

  const [rowsByWeek, setRowsByWeek] = useState<RowsByWeek>({});
  const [editsMap, setEditsMap] = useState<EditsMap>({});
  const [newRows, setNewRows] = useState<Record<string, PlanRow>>({});

  const [selectedDemos, setSelectedDemos] = useState<Record<string, boolean>>({ hh: true });
  const [selectedMeasurement, setSelectedMeasurement] = useState<string>('Live+ Same Day AA');
  const [warning, setWarning] = useState<Record<string, boolean>>({});

  const [selectedView, setSelectedView] = useState<SelectedView>('table');
  const [selectedNetworks, setSelectedNetwork, setSelectedNetworks] = useMap<string, boolean>({});
  const [saving, setSaving] = useState(false);
  const [generalLoading, setGeneralLoading] = useState(false);

  const [showPendingChanges, setShowPendingChanges] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [showImportChangesModal, setShowImportChangesModal] = useState(false);
  const [showNoRateChangesModal, setShowNoRateChangesModal] = useState(false);
  const [showNewNetworkModal, setShowNewNetworkModal] = useState(false);
  const [showBlanketTrafficModal, setShowBlanketTrafficModal] = useState(false);
  const [showXMLImportModal, setShowXMLImportModal] = useState(false);
  const [showIconLocalImportModal, setShowIconLocalImportModal] = useState(false);
  const [showExtendSelectedModal, setShowExtendSelectedModal] = useState(false);
  const [showAudiencePickerModal, setShowAudiencePickerModal] = useState(false);
  const [dates, setDates] = useState(defaultDates);
  const [extendEditModalData, setExtendEditModalData] = useState<ExtendEditModalData | undefined>();
  const [selectedRows, setSelectedRows] = useState<Record<string, PlanRow>>({});
  const [mondayDates, setMondayDates] = useState<string[]>([]);
  // One row per week the rotation covers — a single-week rotation is the one-element case.
  const [creativeAllocationModalData, setCreativeAllocationModalData] = useState<PlanRow[]>();
  const enableAllLinearBuyingChanges = Boolean(useExperimentFlag('enableAllLinearBuyingChanges'));
  const enablePastLinearBuyingEdits = Boolean(useExperimentFlag('enablePastLinearBuyingEdits'));
  const enableBlanketTraffic = Boolean(useExperimentFlag('enableBlanketTraffic'));
  const allowPastLinearEdits = isAdmin || enablePastLinearBuyingEdits;
  // update date array based on user input
  useEffect(() => {
    const mondayArray: string[] = [];
    let newDate = Dfns.parseISO(dates.start);
    const endDate = Dfns.parseISO(dates.end);
    setMondayDates([]);
    while (newDate <= endDate) {
      mondayArray.push(Dfns.format(DATE_FORMAT, newDate));
      newDate = Dfns.addDays(7, newDate);
    }
    setMondayDates(prevState => [...prevState, ...mondayArray]);
  }, [dates]);

  // get our plan rows
  const ourPlanRows: PlanRow[] = useMemo(() => {
    if (!R.isEmpty(rowsByWeek) && compareDates(rowsByWeek, mondayDates)) {
      const newRowsForCurrentWeek = R.filter(
        row => R.includes(row.week, mondayDates),
        R.values(newRows)
      );
      let combinedRows: PlanRow[] = [];
      mondayDates.forEach(week => {
        combinedRows.push(...rowsByWeek[week]);
      });
      combinedRows = [...newRowsForCurrentWeek, ...combinedRows];
      return combinedRows;
    }
    return [];
  }, [rowsByWeek, newRows, mondayDates]);

  // One instruction per ICON network/avail/buy-type actually in the buy for the weeks on screen.
  const blanketCombos = useMemo(() => getBlanketCombos(ourPlanRows), [ourPlanRows]);

  const closeBlanketTrafficModal = useCallback(() => setShowBlanketTrafficModal(false), []);

  const updateRowsByWeek = useCallback(
    (rows: PlanRow[]) => {
      setRowsByWeek(current => {
        const newState = { ...current };
        mondayDates.forEach(week => {
          const matchingDateRows = R.filter(row => row.week === week, rows);
          newState[week] = matchingDateRows;
        });
        return newState;
      });
    },
    [mondayDates]
  );

  // Selected demos are sent to the estimates lambda as a comma-joined list.
  const joinedDemos = useMemo(() => Object.keys(selectedDemos).join(','), [selectedDemos]);

  const {
    data: plansData,
    isLoading: plansLoading,
    error: plansError
  } = useLinearPlans(company, dates.start, dates.end, channel);
  const {
    data: nielsenData,
    isFetching: nielsenFetching,
    error: nielsenError
  } = useNielsenEstimates(joinedDemos, selectedMeasurement);
  const { data: networks, error: networksError } = useLinearNetworks(channel);
  const {
    data: rotationsAndPricingData,
    isPending: ratesPending,
    error: ratesError
  } = useLinearRates(company, networks);
  const { data: constraints } = useLinearConstraints();
  const {
    data: blanketTraffic,
    isLoading: blanketTrafficLoading,
    refetch: refetchBlanketTraffic,
    error: blanketTrafficError
  } = useBlanketTraffic(enableBlanketTraffic ? company : '');

  // A covered row draws its rotation from the blanket instruction, so the grid shouldn't flag it
  // as missing one. Undefined when the feature is off, which leaves the warning untouched.
  const savedBlanketInstructions = blanketTraffic?.blanketTraffic;
  const isBlanketCovered = useCallback(
    (row: PlanRow) => Boolean(findCoveringInstruction(savedBlanketInstructions ?? [], row)),
    [savedBlanketInstructions]
  );
  const { data: extendEditRowOptions, error: extendEditRowOptionsError } = useFutureMatchingPlans(
    company,
    extendEditModalData?.row
  );

  // Rates use `isPending`, not `isLoading`: the query stays disabled until `/networks` resolves,
  // and a disabled query reports `isLoading: false`, so the spinner could clear while
  // `rotationsAndPricing` was still empty and the avail/rotation/type dropdowns rendered blank.
  // Nielsen is deliberately NOT in here — that lambda is slow, and blocking the whole page on it
  // would hide rows that are perfectly usable without audience numbers. The audience columns show
  // their own loading state instead, and the exports below wait for it.
  const isLoading = plansLoading || ratesPending;
  // Exports bake the estimates into the file, so an export taken mid-fetch would silently ship
  // zeroed (or the previous demo's) impressions and CPMs.
  const nielsenReady = Boolean(nielsenData) && !nielsenFetching;
  const campaigns = useMemo(() => plansData?.campaigns ?? [], [plansData]);
  const assumedClearanceRate = plansData?.assumedClearanceRate;
  const rotationsAndPricing = useMemo(
    () => rotationsAndPricingData ?? {},
    [rotationsAndPricingData]
  );
  const nielsenEstimates = useMemo(() => nielsenData?.estimatesByHH ?? {}, [nielsenData]);
  const universeEstimate = nielsenData?.universeEstimate;

  // The table works against an editable copy of the fetched rows, so seed it from the query
  // rather than mutating the cache.
  useEffect(() => {
    if (plansData?.rows) {
      updateRowsByWeek(plansData.rows);
    }
  }, [plansData, updateRowsByWeek]);

  useEffect(() => {
    const failures: [unknown, string][] = [
      [plansError, `Failed to get linear plans for w/o ${dates.start} - ${dates.end}`],
      [nielsenError, 'Failed to get Nielsen estimates'],
      [networksError, 'Failed to get networks'],
      [ratesError, 'Failed to get linear rates'],
      [
        extendEditRowOptionsError,
        `Failed to get future plans for plan ID ${extendEditModalData?.row.plan_id}`
      ],
      [blanketTrafficError, 'Failed to get blanket traffic instructions']
    ];
    for (const [error, message] of failures) {
      if (error) {
        setError({
          message: `${message}. Error: ${error instanceof Error ? error.message : String(error)}`,
          reportError: error
        });
      }
    }
  }, [
    plansError,
    nielsenError,
    networksError,
    ratesError,
    extendEditRowOptionsError,
    blanketTrafficError,
    dates,
    extendEditModalData,
    setError
  ]);

  const filterBarLines = useMemo(() => {
    const campaignMap = campaigns.reduce(
      (acc, campaign) => ({ ...acc, [campaign.id]: campaign.campaign }),
      {}
    );
    return (ourPlanRows || []).map((row: PlanRow) => {
      const typeName = TYPES_TO_NAMES[row.type];
      let campaignName;
      if (row.campaign_id) {
        campaignName = campaignMap[row.campaign_id];
      }
      return { ...row, typeName, campaignName };
    });
  }, [ourPlanRows, campaigns]);

  const [filter, setFilter] = useStateFunction<(line) => boolean>(() => true);

  const filteredRowData = useMemo(() => {
    if (filterBarLines) {
      const filtered = filterBarLines.filter(filter);
      return filtered;
    }
    return [];
  }, [filterBarLines, filter]);

  useEffect(() => {
    if (!mondayDates.length) {
      return;
    }

    const networksWithoutTraffic: Record<string, boolean> = {};

    const creativeRows = flattenedRowsByWeek(rowsByWeek);
    for (const row of creativeRows) {
      if (!row.creatives) {
        networksWithoutTraffic[row.network] = true;
      }
    }
    setWarning(networksWithoutTraffic);
  }, [mondayDates, rowsByWeek]);

  const campaignMetadata: CampaignMetadata = useMemo(() => {
    const options = R.map(
      campaign => ({ label: campaign.campaign, value: campaign.id }),
      campaigns
    );
    const idToName = campaigns.reduce((prev, curr) => ({ ...prev, [curr.id]: curr.campaign }), {});
    const nameToId = campaigns.reduce((prev, curr) => ({ ...prev, [curr.campaign]: curr.id }), {});
    return { options, idToName, nameToId };
  }, [campaigns]);

  const addNewRow = (network: string, week: string, isNewNetwork = false) => {
    const newRow = makeNewRow(rotationsAndPricing, network, week, company, isNewNetwork);

    setNewRows(current => ({ ...current, [newRow.key]: newRow }));
  };

  const addNewNetwork = (network: string) => {
    mondayDates.forEach(week => {
      addNewRow(network, week, true);
    });
    setShowNewNetworkModal(false);
    setSelectedNetwork(network, !selectedNetworks[network]);
  };

  const bulkImport = useCallback(
    async (file, week) => {
      try {
        if (!rowsByWeek[week]) {
          const { rows } = await fetchLinearPlans(company, dates.start, dates.end, channel);
          updateRowsByWeek(rows);
          const importedNewRows = await processBulkImport(
            file,
            mondayDates,
            company,
            rows,
            creativeMap,
            rotationsAndPricing,
            campaignMetadata.nameToId,
            enableAllLinearBuyingChanges
          );
          setNewRows(current => ({ ...current, ...importedNewRows }));
        } else {
          const importedNewRows = await processBulkImport(
            file,
            mondayDates,
            company,
            filteredRowData,
            creativeMap,
            rotationsAndPricing,
            campaignMetadata.nameToId,
            enableAllLinearBuyingChanges
          );
          setNewRows(current => ({ ...current, ...importedNewRows }));
        }
      } catch (e) {
        setError({
          message: `Failed to import buy for the w/o ${week}: ${
            e instanceof Error ? e.message : String(e)
          }`,
          reportError: e
        });
      }
    },
    [
      updateRowsByWeek,
      company,
      creativeMap,
      rowsByWeek,
      setError,
      rotationsAndPricing,
      campaignMetadata.nameToId,
      dates,
      mondayDates,
      filteredRowData,
      enableAllLinearBuyingChanges,
      channel
    ]
  );

  const importXML = useCallback(
    async (file, week) => {
      try {
        if (!rowsByWeek[week]) {
          const { rows } = await fetchLinearPlans(company, dates.start, dates.end, channel);
          updateRowsByWeek(rows);
        }
        await processXMLImport(file, company);
      } catch (e) {
        setError({
          message: `Failed to import buy for the w/o ${week}: ${
            e instanceof Error ? e.message : String(e)
          }`,
          reportError: e
        });
      }
    },
    [setError, company, updateRowsByWeek, rowsByWeek, dates, channel]
  );

  const importChanges = useCallback(
    async (file, week) => {
      try {
        // Rows already on screen are the ones we edit against. If the imported week isn't loaded,
        // pull it first so there's something for the import to match.
        let existingRows: PlanRow[] = filteredRowData;
        if (!rowsByWeek[week]) {
          const { rows } = await fetchLinearPlans(company, dates.start, dates.end, channel);
          updateRowsByWeek(rows);
          existingRows = rows;
        }
        const importedChanges = await processImportChanges(
          file,
          existingRows,
          creativeMap,
          campaignMetadata.idToName,
          company,
          mondayDates,
          enableAllLinearBuyingChanges
        );
        setEditsMap(current => ({ ...current, ...importedChanges }));
      } catch (e) {
        setError({
          message: `Failed to import changes for the w/o ${week}: ${
            e instanceof Error ? e.message : String(e)
          }`,
          reportError: e
        });
      }
    },
    [
      updateRowsByWeek,
      rowsByWeek,
      company,
      creativeMap,
      campaignMetadata.idToName,
      setError,
      mondayDates,
      dates,
      filteredRowData,
      enableAllLinearBuyingChanges,
      channel
    ]
  );

  const iconLocalImport = useCallback(
    async (file: ArrayBuffer) => {
      try {
        const mappings = await fetchIconLocalMappings();

        const existingRows = filteredRowData.length > 0 ? filteredRowData : [];
        const result = await processIconLocalImport(file, company, existingRows, mappings, channel);

        const { missingMappings } = result;
        const hasMissing =
          missingMappings.markets.length > 0 ||
          missingMappings.networks.length > 0 ||
          missingMappings.dayparts.length > 0 ||
          missingMappings.dows.length > 0;

        if (hasMissing) {
          const sections: string[] = [];
          if (missingMappings.markets.length > 0) {
            sections.push(`Markets: ${missingMappings.markets.join(', ')}`);
          }
          if (missingMappings.networks.length > 0) {
            sections.push(`Networks (Stations): ${missingMappings.networks.join(', ')}`);
          }
          if (missingMappings.dayparts.length > 0) {
            sections.push(`Dayparts: ${missingMappings.dayparts.join(', ')}`);
          }
          if (missingMappings.dows.length > 0) {
            sections.push(`Days of Week: ${missingMappings.dows.join(', ')}`);
          }
          setError({
            message: `ICON Local import aborted. The following mappings are missing. Please add them on the ICON Local Linear Mappings page and re-import.\n\n${sections.join('\n')}`
          });
          return;
        }

        setNewRows(current => ({ ...current, ...result.newRows }));
      } catch (e) {
        setError({
          message: `Failed to import ICON Local plan: ${
            e instanceof Error ? e.message : String(e)
          }`,
          reportError: e
        });
      }
    },
    [company, filteredRowData, setError, channel]
  );

  // checking if rows by week has the correct data and then processing extend selected
  const extendSelected = useCallback(
    async (rowsToExtend, extendedWeek) => {
      try {
        if (!rowsByWeek[extendedWeek]) {
          const { rows } = await fetchLinearPlans(company, extendedWeek, extendedWeek, channel);
          updateRowsByWeek(rows);
          const extendedRows = processExtendSelected(rowsToExtend, extendedWeek, rows);
          setNewRows(current => ({ ...current, ...extendedRows }));
        } else {
          const extendedRows = processExtendSelected(
            rowsToExtend,
            extendedWeek,
            rowsByWeek[extendedWeek]
          );

          setNewRows(current => ({ ...current, ...extendedRows }));
        }
      } catch (e) {
        setError({
          message: `Failed to extend selected rows for the w/o ${extendedWeek}: ${
            e instanceof Error ? e.message : String(e)
          }`,
          reportError: e
        });
      }
    },
    [updateRowsByWeek, company, rowsByWeek, setError, channel]
  );

  const refreshRatesForSelected = useCallback(() => {
    const rateChanges = checkUpdatedRates(rotationsAndPricing, Object.values(selectedRows));

    if (R.isEmpty(rateChanges)) {
      setShowNoRateChangesModal(true);
      return;
    }
    setEditsMap(current => R.mergeDeepRight(current, rateChanges) as EditsMap);
    setSelectedRows({});
    setShowPendingChanges(true);
  }, [rotationsAndPricing, selectedRows]);

  /*
   * Copy selected plan rows and add them as pending new rows.
   */
  const copyRows = useCallback(() => {
    const copiedRows = Object.fromEntries(
      Object.values(selectedRows).map(value => {
        const newKey = makeKeyWithUUID(value);
        return [
          newKey,
          {
            ...value,
            key: newKey,
            plan_id: null,
            order_id: null,
            traffic_id: null,
            isNewNetwork: true,
            isNewRow: true,
            is_plan_pending: true
          }
        ];
      })
    );
    if (!R.isEmpty(copiedRows)) {
      setNewRows(current => ({ ...current, ...copiedRows }));
    }
    setSelectedRows({});
  }, [selectedRows]);

  const hasPendingChanges = useMemo(() => {
    if (!R.isEmpty(editsMap) || !R.isEmpty(newRows)) {
      return true;
    }
    return false;
  }, [editsMap, newRows]);

  const clearAllChanges = () => {
    setNewRows({});
    setEditsMap({});
    setShowPendingChanges(false);
  };

  const saveChanges = async () => {
    setSaving(true);
    const { hasDuplicates, lines } = checkForDuplicateRows(rowsByWeek, editsMap, newRows);
    const { isViolatingConstraints, rows } = checkForConstraints(editsMap, newRows, constraints);
    const { isUpdatingImpressions, listOfRows } = checkForImpressionOverrides(editsMap, newRows);
    if (isUpdatingImpressions) {
      try {
        await setAreYouSure({
          title: 'You are overriding impressions or Nielsen Estimates.',
          message: listOfRows,
          okayText: 'Save anyway'
        });
      } catch {
        setSaving(false);
        return;
      }
    }
    if (hasDuplicates) {
      setError({ title: 'The following rows are duplicated', message: lines });
      setSaving(false);
      return;
    }
    if (isViolatingConstraints) {
      try {
        await setAreYouSure({
          title: 'You are violating some constraints.',
          message: rows,
          okayText: 'Save anyway'
        });
      } catch {
        setSaving(false);
        return;
      }
    }

    try {
      await saveLinearPlans({
        editedRows: R.values(editsMap),
        newRows: R.values(newRows),
        company,
        useRadioExperiment: channel === 'radio',
        useLinearBuyingUpdatesExperiment: enableAllLinearBuyingChanges
      });
      // `rowsByWeek` is deliberately left alone: the refetch re-seeds it through the `plansData`
      // effect. Clearing it here would blank the table whenever the refetched rows are deeply
      // equal to what's cached — React Query's structural sharing hands back the same object, so
      // the effect never fires and nothing puts the rows back.
      await queryClient.invalidateQueries({ queryKey: LINEAR_PLANS_QUERY_KEY() });
      setEditsMap({});
      setNewRows({});
      setSelectedNetworks({});
      setSelectedRows({});
      setSelectedView('table');
      setSaving(false);
      setShowPendingChanges(false);
    } catch (e) {
      setSaving(false);
      setError({
        message: e instanceof Error ? e.message : String(e),
        reportError: e
      });
    }
  };

  const renderItem =
    selectedView === 'summary' ? (
      <SummaryView
        rows={filteredRowData}
        editsMap={editsMap}
        newRows={newRows}
        creativeMap={creativeMap}
        weeks={mondayDates}
        nielsenEstimates={nielsenEstimates}
        isLoading={!nielsenReady}
      />
    ) : (
      <div className='table-view-container'>
        <div className='filter-bar-container'>
          <FilterBar
            size={Size.LG}
            options={FILTER_BAR_OPTIONS}
            lines={filterBarLines}
            onFilter={setFilter}
            hasAdvanced
          />
        </div>
        <EditTableView
          channel={channel}
          rows={filteredRowData}
          editsMap={editsMap}
          rotationsAndPricing={rotationsAndPricing}
          nielsenEstimates={nielsenEstimates}
          nielsenLoading={nielsenFetching}
          selectedDemos={selectedDemos}
          selectedMeasurement={selectedMeasurement}
          universeEstimate={universeEstimate}
          campaignMetadata={campaignMetadata}
          weeks={mondayDates}
          selectedRows={selectedRows}
          setNewRows={setNewRows}
          setEditsMap={setEditsMap}
          setSelectedRows={setSelectedRows}
          setCreativeAllocationModalData={setCreativeAllocationModalData}
          isBlanketCovered={enableBlanketTraffic ? isBlanketCovered : undefined}
        />
      </div>
    );

  const getNMIExportBooked = useCallback(async () => {
    try {
      setGeneralLoading(true);
      const response = await fetchBookedNielsenEstimates({
        company,
        demos: joinedDemos,
        measurement: selectedMeasurement,
        weekStart: dates.start,
        weekEnd: dates.end,
        assumedClearanceRate
      });
      downloadJSONToCSV(
        response,
        `NMI_${company}_${dates.start}_${joinedDemos}_${selectedMeasurement}`
      );
      setGeneralLoading(false);
    } catch (e) {
      setGeneralLoading(false);
      setError({
        message: `Failed to get NMI Export. Error: ${e instanceof Error ? e.message : String(e)}`,
        reportError: e
      });
    }
  }, [company, dates, joinedDemos, selectedMeasurement, assumedClearanceRate, setError]);

  const getNMIExportDelivered = useCallback(async () => {
    try {
      setGeneralLoading(true);
      const response = await fetchDeliveredNielsenEstimates({
        company,
        demos: joinedDemos,
        measurement: selectedMeasurement,
        weekStart: dates.start,
        weekEnd: dates.end
      });
      downloadJSONToCSV(
        response,
        `NMI_Delivered_${company}_${dates.start}_${joinedDemos}_${selectedMeasurement}`
      );
      setGeneralLoading(false);
    } catch (e) {
      setGeneralLoading(false);
      setError({
        message: `Failed to get NMI Export. Error: ${e instanceof Error ? e.message : String(e)}`,
        reportError: e
      });
    }
  }, [company, dates, joinedDemos, selectedMeasurement, setError]);

  const canEditWeek =
    dates.start >= CURRENT_WEEK_START || allowPastLinearEdits || channel === 'radio';

  const actionOptions: DropdownOption<LinearBuyingActionType>[] = [
    ...(canEditWeek
      ? [
          LinearBuyingAction.BULK_IMPORT,
          LinearBuyingAction.IMPORT_CHANGES,
          LinearBuyingAction.IMPORT_ICON_LOCAL_PLAN,
          LinearBuyingAction.EXTEND_SELECTED,
          LinearBuyingAction.REFRESH_RATES,
          LinearBuyingAction.COPY_ROWS
        ]
      : []),
    { value: LinearBuyingAction.EXPORT, disabled: !nielsenReady },
    { value: LinearBuyingAction.EXPORT_SUMMARY, disabled: !nielsenReady },
    LinearBuyingAction.NMI_BOOKED_EXPORT,
    LinearBuyingAction.NMI_DELIVERED_EXPORT,
    LinearBuyingAction.XML_IMPORT,
    ...(enableBlanketTraffic && blanketCombos.length > 0
      ? [LinearBuyingAction.BLANKET_TRAFFIC_INSTRUCTIONS]
      : [])
  ];

  const actionHandlers: Record<LinearBuyingActionType, () => void> = {
    [LinearBuyingAction.BULK_IMPORT]: () => setShowBulkImportModal(true),
    [LinearBuyingAction.IMPORT_CHANGES]: () => setShowImportChangesModal(true),
    [LinearBuyingAction.IMPORT_ICON_LOCAL_PLAN]: () => setShowIconLocalImportModal(true),
    [LinearBuyingAction.EXTEND_SELECTED]: () => setShowExtendSelectedModal(true),
    [LinearBuyingAction.REFRESH_RATES]: refreshRatesForSelected,
    [LinearBuyingAction.COPY_ROWS]: copyRows,
    [LinearBuyingAction.EXPORT]: () => {
      exportToExcel(
        ourPlanRows,
        creativeMap,
        nielsenEstimates,
        universeEstimate,
        campaignMetadata.idToName,
        company,
        dates.start,
        enableAllLinearBuyingChanges
      ).catch(e =>
        setError({
          message: `Failed to export: ${e instanceof Error ? e.message : String(e)}`,
          reportError: e
        })
      );
    },
    [LinearBuyingAction.EXPORT_SUMMARY]: () => {
      exportSummaryView(
        rowsByWeek[dates.start],
        editsMap,
        newRows,
        creativeMap,
        company,
        dates.start,
        nielsenEstimates,
        assumedClearanceRate
      ).catch(e =>
        setError({
          message: `Failed to export summary: ${e instanceof Error ? e.message : String(e)}`,
          reportError: e
        })
      );
    },
    [LinearBuyingAction.NMI_BOOKED_EXPORT]: getNMIExportBooked,
    [LinearBuyingAction.NMI_DELIVERED_EXPORT]: getNMIExportDelivered,
    [LinearBuyingAction.XML_IMPORT]: () => setShowXMLImportModal(true),
    [LinearBuyingAction.BLANKET_TRAFFIC_INSTRUCTIONS]: () => {
      setShowBlanketTrafficModal(true);
      Mixpanel.track(MxE.ModalOpened, { page: 'Linear Buying', modal: 'blanket traffic' });
    }
  };

  return (
    <Page
      title={
        <div className='linear-buying-title'>
          <div>{channel === 'radio' ? 'Radio Buying' : 'Linear Buying'}</div>
          <Dropdown
            type={DropdownToggleType.ICON_ONLY}
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            className='options-dropdown'
            ariaLabel='More actions'
            options={actionOptions}
            onChange={action => actionHandlers[action]()}
            hasFilterBar={false}
            leadingIcon={<MdMoreHoriz />}
            placeholder=''
            stretch={StretchMode.FIT}
          />
          {generalLoading && <Spinner />}
          {canEditWeek && selectedView === 'table' && (
            <Button
              appearance={Appearance.OUTLINED}
              design={Design.NEUTRAL}
              background={Background.DARK}
              onClick={() => setShowNewNetworkModal(true)}
            >
              + Add Row
            </Button>
          )}
          <Button
            appearance={Appearance.OUTLINED}
            design={Design.NEUTRAL}
            background={Background.DARK}
            onClick={() => setShowAudiencePickerModal(true)}
          >
            Demos:{' '}
            {R.keys(selectedDemos).length === 1 &&
            R.values(DEMO_GROUPINGS.Meta).includes(R.keys(selectedDemos)[0])
              ? PRETTY_DEMO_NAMES[R.keys(selectedDemos)[0]]
              : 'Custom'}
          </Button>
          <Form.Group className='measurement'>
            <Dropdown
              className='measurement-dropdown'
              size={Size.MD}
              label='Measurement'
              value={selectedMeasurement}
              options={MEASUREMENT_OPTIONS}
              onChange={setSelectedMeasurement}
            />
          </Form.Group>
          {!R.isEmpty(warning) && (
            <div>
              <OverlayTrigger
                placement={Placement.RIGHT_START}
                overlay={
                  <Tooltip id={dates.start}>
                    Networks that have missing traffic ({R.keys(warning).length}):{' '}
                    {R.keys(warning).join(', ')}
                  </Tooltip>
                }
              >
                <IconButton design={Intent.WARNING} size={Size.MD}>
                  <MdWarning color='white' />
                </IconButton>
              </OverlayTrigger>
            </div>
          )}
        </div>
      }
      pageType={channel === 'radio' ? 'Radio Buying' : 'Linear Buying'}
      isLoading={isLoading}
      actions={
        <div className='linear-buying-actions'>
          <ButtonGroup>
            <Button
              appearance={selectedView === 'table' ? Appearance.FILLED : Appearance.OUTLINED}
              design={Design.NEUTRAL}
              background={Background.DARK}
              onClick={() => {
                setSelectedView('table');
                setShowPendingChanges(false);
              }}
            >
              Table View
            </Button>
            <Button
              appearance={selectedView === 'summary' ? Appearance.FILLED : Appearance.OUTLINED}
              design={Design.NEUTRAL}
              background={Background.DARK}
              onClick={() => {
                setSelectedView('summary');
                setShowPendingChanges(false);
              }}
            >
              Summary View
            </Button>
          </ButtonGroup>
          <DatePicker
            bordered
            range={dates}
            fullWeeksOnly
            isOutsideRange={() => false}
            isDayBlocked={date => !R.pipe(Dfns.parseISO, Dfns.isMonday)(date)}
            onChange={newDates => {
              setDates(newDates);
              setSelectedNetworks({});
              setSelectedRows({});
            }}
          />
        </div>
      }
      subHeader={
        /*
          The change controls live in their own bar rather than the header. The header centers the
          title and the actions against each other, so a second row inside it can only be centered
          by reserving matching empty space above — which pushed everything out of alignment. This
          bar is a sibling of the header, so it can't disturb it.

          Always rendered, hidden until there's something to save, so the bar's height is reserved
          and nothing below it moves. `visibility: hidden` also drops these out of the tab order and
          ignores clicks, so there's nothing reachable while they're invisible.
        */
        <div className={cn('linear-buying-sub-header', { 'is-hidden': !hasPendingChanges })}>
          <Button
            appearance={Appearance.OUTLINED}
            design={Design.NEUTRAL}
            leadingElement={<MdFilterList />}
            onClick={() => setShowPendingChanges(!showPendingChanges)}
          >
            Pending Changes
          </Button>
          <Button design={Intent.ERROR} onClick={clearAllChanges}>
            Clear All Changes
          </Button>
          <IconButton
            aria-label='Save changes'
            size={Size.MD}
            design={Intent.SUCCESS}
            loading={saving}
            onClick={saveChanges}
          >
            <MdSave />
          </IconButton>
        </div>
      }
    >
      <div className='linear-buying'>
        <div className='left-side-container'>{isLoading ? <Spinner /> : renderItem}</div>
        {showPendingChanges && (
          <PendingChanges
            editsMap={editsMap}
            newRows={newRows}
            setEditsMap={setEditsMap}
            setNewRows={setNewRows}
            setExtendEditModalData={setExtendEditModalData}
            setShowPendingChanges={setShowPendingChanges}
          />
        )}
      </div>
      {showBulkImportModal && (
        <ImportModal
          importType='bulk'
          importFunction={bulkImport}
          week={dates.start}
          onClose={() => setShowBulkImportModal(false)}
        />
      )}
      {showXMLImportModal && (
        <ImportModal
          importType='xml'
          importFunction={importXML}
          week={dates.start}
          onClose={() => setShowXMLImportModal(false)}
        />
      )}
      {showImportChangesModal && (
        <ImportModal
          importType='changes'
          importFunction={importChanges}
          week={dates.start}
          onClose={() => setShowImportChangesModal(false)}
        />
      )}
      {showIconLocalImportModal && (
        <IconLocalImportModal
          importFunction={iconLocalImport}
          onClose={() => setShowIconLocalImportModal(false)}
        />
      )}
      {showNoRateChangesModal && (
        <NoRateChangesModal onClose={() => setShowNoRateChangesModal(false)} />
      )}
      {showNewNetworkModal && (
        <AddNewNetworkModal
          rotationsAndPricing={rotationsAndPricing}
          addNewNetwork={addNewNetwork}
          onClose={() => setShowNewNetworkModal(false)}
        />
      )}
      {showBlanketTrafficModal && creativeMap && (
        <BlanketTrafficModal
          company={company}
          defaultWeek={dates.start}
          planRows={ourPlanRows}
          instructions={blanketTraffic?.blanketTraffic ?? []}
          isLoading={blanketTrafficLoading}
          creativeMap={creativeMap}
          colorMap={colorMap}
          onSaved={refetchBlanketTraffic}
          onClose={closeBlanketTrafficModal}
        />
      )}
      {extendEditModalData && extendEditRowOptions && (
        <ExtendEditModal
          onClose={() => setExtendEditModalData(undefined)}
          editData={extendEditModalData}
          rowOptions={extendEditRowOptions}
          setEditsMap={setEditsMap}
        />
      )}
      {showExtendSelectedModal && (
        <ExtendSelectedModal
          onClose={() => {
            setShowExtendSelectedModal(false);
            setSelectedRows({});
          }}
          rowsToExtend={selectedRows}
          week={dates.start}
          extendFunction={extendSelected}
        />
      )}
      {creativeAllocationModalData && creativeAllocationModalData.length > 0 && creativeMap && (
        <CreativeAllocationModal
          planRows={creativeAllocationModalData}
          creativeMap={creativeMap}
          colorMap={colorMap}
          setEditsMap={setEditsMap}
          setNewRows={setNewRows}
          isBlanketCovered={enableBlanketTraffic ? isBlanketCovered : undefined}
          onClose={() => setCreativeAllocationModalData(undefined)}
        />
      )}
      {showAudiencePickerModal && (
        <AudiencePickerModal
          selectedDemos={selectedDemos}
          setSelectedDemos={setSelectedDemos}
          onClose={() => setShowAudiencePickerModal(false)}
        />
      )}
    </Page>
  );
};
